import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin, User, CalendarBlank, Check, X } from '@phosphor-icons/react';
import { Card, CardContent } from 'components/ui/card';
import { FlowIndicator } from './FlowIndicator';
import { IncomingUnit } from '../hooks/useHerverhuurPipeline';

interface IncomingInspectionCardProps {
  unit: IncomingUnit;
}

const formatDate = (date: string | null) => {
  if (!date) return null;
  try {
    return format(new Date(date), 'd MMM yyyy', { locale: nl });
  } catch {
    return null;
  }
};

export const IncomingInspectionCard = ({ unit }: IncomingInspectionCardProps) => {
  const inspectionDate = formatDate(unit.incomingInspectionDate);

  const checklist = [
    { label: 'Contract getekend', checked: unit.incomingContractSigned },
    { label: 'Inspectie', checked: !!unit.incomingInspectionDate, value: inspectionDate ?? undefined },
    { label: 'Waarborg ontvangen', checked: unit.incomingGuaranteeReceived },
    { label: 'Brandverzekering', checked: unit.incomingFireInsuranceReceived },
  ];

  const completedCount = checklist.filter(c => c.checked).length;

  return (
    <Card className="border border-border shadow-sm hover:shadow-md transition-shadow cursor-default">
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-start gap-1.5 min-w-0">
          <MapPin className="text-neutral-secondary-mid mt-0.5 flex-shrink-0" size={13} weight="fill" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-neutral-primary leading-tight truncate">
              {unit.buildingName || unit.buildingId}
            </p>
            <p className="text-[11px] text-neutral-secondary-mid">
              {unit.unitLabel} • {unit.floor !== null ? `Verd. ${unit.floor}` : '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <User className="text-neutral-secondary-mid flex-shrink-0" size={13} />
          <p className="text-xs text-neutral-primary truncate">
            {unit.tenantName || <span className="text-neutral-secondary-mid italic">Onbekend</span>}
          </p>
        </div>

        {inspectionDate && (
          <div className="flex items-center gap-1.5">
            <CalendarBlank className="text-neutral-secondary-mid flex-shrink-0" size={13} />
            <p className="text-xs text-neutral-secondary-mid">
              PB: <span className="text-neutral-primary">{inspectionDate}</span>
            </p>
          </div>
        )}

        <div className="flex flex-col gap-1 pt-1 border-t border-border">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] text-neutral-secondary-mid">Voortgang</span>
            <span className="text-[10px] font-medium text-moss-primary">{completedCount}/{checklist.length}</span>
          </div>
          {checklist.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              {item.checked ? (
                <Check className="text-moss-primary flex-shrink-0" size={12} weight="bold" />
              ) : (
                <X className="text-neutral-secondary-mid flex-shrink-0" size={12} weight="bold" />
              )}
              <span className="text-[11px] text-neutral-secondary-mid">
                {item.label}
                {item.value && <span className="text-neutral-primary ml-1">{item.value}</span>}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center pt-1">
          <FlowIndicator activeStep={5} />
        </div>
      </CardContent>
    </Card>
  );
};
